// src/pages/AboutUs.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import CommonLayout from "../../../Components/CommonLayout/CommonLayout";
import { toast } from "react-toastify";
import { TemplateApi } from "../../../App/api/setting";

const TABS = [
  { id: "customer", label: "Customer", field: "customerAboutUs" },
  { id: "delivery", label: "Delivery Partner", field: "deliveryManAboutUs" },
  { id: "store", label: "Store", field: "storeAboutUs" },
];

const AboutUs = () => {
  const { theme } = useSelector((state) => state.theme);

  const [activeTab, setActiveTab] = useState("customer");
  const [aboutUsDoc, setAboutUsDoc] = useState(null);
  const [textValue, setTextValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const currentField = useMemo(
    () => TABS.find((t) => t.id === activeTab)?.field,
    [activeTab]
  );

  const currentContent = aboutUsDoc?.[currentField]?.trim() || "";

  // Load data
  const loadAboutUs = async () => {
    try {
      setFetching(true);
      const res = await TemplateApi.aboutUs.getAllAboutUs();
      const docs = res.data?.data || res.data || [];
      setAboutUsDoc(docs[0] || null);
    } catch {
      toast.error("Failed to load About Us");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    loadAboutUs();
  }, []);

  // Reset editing state when tab changes
  useEffect(() => {
    setIsEditing(false);
    setTextValue(currentContent);
  }, [activeTab, currentContent]);

  // Enter edit mode
  const handleEdit = () => {
    setIsEditing(true);
    setTextValue(currentContent);
  };

  // Save changes
  const handleSave = async () => {
    if (!textValue.trim()) {
      toast.error("Content cannot be empty");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        [currentField]: textValue.trim(),
      };

      if (aboutUsDoc?._id) {
        await TemplateApi.aboutUs.updateAboutUs(payload, aboutUsDoc._id);
        toast.success("Updated successfully");
      } else {
        await TemplateApi.aboutUs.createAboutUs(payload);
        toast.success("Saved successfully");
      }

      await loadAboutUs();
      setIsEditing(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  // Cancel edit
  const handleCancel = () => {
    setIsEditing(false);
    setTextValue(currentContent);
  };

  // Common text styling classes (same for read & edit mode)
  const textBaseClass = `whitespace-pre-wrap leading-relaxed text-lg font-normal
    ${theme ? "text-gray-800" : "text-gray-200"}`;

  const textareaClass = `w-full p-4 rounded border resize-y focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg font-normal
    ${theme ? "bg-white text-gray-900 border-gray-300" : "bg-gray-700 text-white border-gray-600"}`;

  return (
    <div className={`min-h-screen ${theme ? "bg-gray-50" : "bg-gray-950"}`}>
      <CommonLayout />

      <div className="max-w-5xl mx-auto p-6">
        <h1 className={`text-3xl font-bold mb-8 ${theme ? "text-gray-900" : "text-white"}`}>
          Manage About Us
        </h1>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-gray-200 dark:border-gray-700">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 border-b-2 font-medium transition-colors
                ${
                  activeTab === tab.id
                    ? "border-green-600 text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className={`p-6 rounded-xl shadow ${theme ? "bg-white" : "bg-gray-800"}`}>
          {fetching ? (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">
              Loading content...
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-2xl font-semibold ${theme ? "text-gray-800" : "text-gray-100"}`}>
                  {TABS.find((t) => t.id === activeTab)?.label}
                </h2>
              </div>

              {isEditing ? (
                // ─── Edit Mode ───
                <div className="space-y-6">
                  <textarea
                    rows={12}
                    value={textValue}
                    onChange={(e) => setTextValue(e.target.value)}
                    placeholder="Write your About Us content here..."
                    className={textareaClass}
                  />

                  <div className="flex gap-4 justify-end">
                    <button
                      onClick={handleCancel}
                      className="px-8 py-3 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition min-w-[140px]"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleSave}
                      disabled={loading || !textValue.trim()}
                      className={`px-8 py-3 rounded-lg text-white font-medium min-w-[140px] transition
                        ${
                          loading || !textValue.trim()
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700"
                        }`}
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                // ─── Read-Only Mode ───
                <div className="space-y-6">
                  {currentContent ? (
                    <div className={textBaseClass}>
                      {currentContent}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400 italic">
                      No content added yet for this section.
                    </div>
                  )}

                  {/* Button on RIGHT side */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleEdit}
                      className={`px-8 py-3 rounded-lg font-medium transition
                        ${
                          theme
                            ? "bg-green-600 hover:bg-green-700 text-white"
                            : "bg-green-700 hover:bg-green-800 text-white"
                        }`}
                    >
                      {currentContent ? "Edit Content" : "Add Content"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutUs;